import { __callbacks } from "Utils/Callbacks/Callback";
import { __utils } from "Utils/Utils";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./Continue.module.scss";
import { MessageNumber } from "../Utils/MessageNumber";
import MessageSend from "components/ChatApp/components/ChatMessage/components/MessageSend/MessageSend";
import ChatMessage from "components/ChatApp/components/ChatMessage/ChatMessage";
import Text from "components/ChatApp/components/ChatMessage/components/Text/Text";
import MessageNotify from "components/ChatApp/components/ChatMessage/components/MessageNotify/MessageNotify";
import MessageReceive from "components/ChatApp/components/ChatMessage/components/MessageReceive/MessageReceive";
import Form from "components/ChatApp/components/ChatMessage/components/Form/Form";
import { __chats } from "Utils/Chats/Chats";
import { IChatMsgAPI, IUnreadMsg } from "Utils/Interfaces";
import FileUpload from "components/ChatApp/components/ChatMessage/components/FileUpload/FileUpload";
import { __map } from "Utils/Map/Map";
import { __chatState } from "Utils/Chats/ChatState";
import { __chatQueue } from "Utils/ChatQueue/ChatQueue";
import { MsgFragmentNewChild } from "Utils/Chats/MsgFragmentNewChild";
import Quiz from "components/ChatApp/components/ChatMessage/components/Quiz/Quiz";
import CustomLayout from "components/ChatApp/components/ChatMessage/components/CustomLayout/CustomLayout";
import CustomForm from "components/ChatApp/components/ChatMessage/components/CustomForm/Form";
import Image from "components/ChatApp/components/ChatMessage/components/Image/Image";
import Navigation from "components/ChatApp/components/ChatMessage/components/Navigation/Navigation";
import { __bot } from "../../../../../../Utils/Bot/Bot";

const components = {
    Text,
    MessageNotify,
    MessageSend,
    MessageReceive,
    Form,
    FileUpload,
    Quiz,
    CustomLayout,
    Image,
    Navigation,
    CustomForm,
    text: Text // Map the lowercase 'text' to 'Text' component
};

const callbackIds = {
    chatInputSubmit: "",
    userOption: "",
    onBotResponse: "",
    onFileUpload: "",
    onUnreadMsgs: "",
};

const currentMsg = {
    messageNo: 0
};

interface ContinueProps {
    chatId?: string;
}

export default function Continue(props: ContinueProps) {
    const container = useRef(null);
    const [chatId, setChatId] = useState<string | undefined>(props.chatId);
    const [messageNo,] = useState(new MessageNumber());
    const [content, setContent] = useState<any[]>([]);

    useEffect(() => {
        //console.log("Props in Continue:", JSON.stringify(props));
        if (!props.chatId) {
            //console.error("Chat ID is missing from props.");
            setChatId("defaultChatId"); // Fallback value
        } else {
            //console.log("Received chatId:", props.chatId);
            setChatId(props.chatId);
        }
    }, [props.chatId]);

    const getProps = useCallback(
        () => {
            return { conversationId: chatId ?? "", messageNo: messageNo.get() };
        },
        [chatId, messageNo]
    );

    const push = useCallback(
        (element: JSX.Element) => {
            messageNo.increment();
            setContent(arr => {
                return [...arr, <ChatMessage key={__utils.getUuidv4()}>{element}</ChatMessage>];
            });
        },
        [messageNo],
    );

    const initFileUploadEvents = useCallback(
        () => {
            if (!chatId) return;

            callbackIds.onFileUpload = __callbacks.onFileUpload((fileData: any) => {

                if (chatId && __chatState.get(chatId)?.disableInputs) {
                    __callbacks.userNotification.trigger("Please follow the instructions of the chat! (Inputs are disabled).");
                    return;
                }

                __callbacks.messageFragment.trigger({
                    newChild: {
                        messageType: "MessageSend",
                        fragmentType: "FileUpload",
                        props: {
                            messageType: "MessageSend",
                            ...__map.mapFileData(fileData),
                            fileData: fileData,
                            ...getProps(),
                            messageNo: currentMsg.messageNo
                        }
                    }
                })
                    .onEmpty(() => {
                        const { messageNo } = getProps();
                        currentMsg.messageNo = messageNo;
                        push(<MessageSend {...getProps()}>{[<FileUpload key={__utils.getRandomIdShort()} {...getProps()} type={"FileUpload"} {...__map.mapFileData(fileData)} fileData={fileData} messageType="MessageSend"></FileUpload>]}</MessageSend>);
                    });
            });
        },
        [getProps, push, chatId],
    );

    const initListeners = useCallback(
        () => {
            if (!chatId) return;

            callbackIds.chatInputSubmit = __callbacks.onChatInputSubmit((data: any) => {

                __callbacks.messageFragment.trigger({
                    newChild: {
                        messageType: "MessageSend",
                        fragmentType: "Text",
                        props: {
                            text: data.textAreaValue
                        }
                    }
                }).onEmpty(() => {
                    const { messageNo } = getProps();
                    currentMsg.messageNo = messageNo;
                    push(<MessageSend {...getProps()}>{[<Text key={__utils.getRandomIdShort()} type={"Text"} text={data.textAreaValue}></Text>]}</MessageSend>);
                });
            });

            initFileUploadEvents();
        },
        [push, getProps, initFileUploadEvents, chatId]
    );

    const formatChat = useCallback(
        (chat: any) => {
            //console.log('Formatting chat:', JSON.stringify(chat));

            if (!chat) {
                console.error('Chat is undefined or null.');
                return;
            }

            const chatMessages: any[] = [];

            chat.forEach((message: any) => {
                const fragments = message["fragments"];
                const elementChilds: any[] = [];

                fragments.forEach((fragment: any) => {
                    try {
                        const Component = (components as any)[fragment.fragmentType];
                        if (!Component) {
                            console.error(`Component for fragmentType: ${fragment.fragmentType} not found.`);
                            return;
                        }
                        const elementChild = React.createElement(Component, { key: __utils.getUuidv4(), ...getProps(), ...fragment, ...fragment.props, local: true });
                        elementChilds.push(elementChild);
                    } catch (error) {
                        console.error(`Error creating element for fragment type: ${fragment.fragmentType}`, error);
                    }
                });

                try {
                    const ParentComponent = (components as any)[message.messageType];
                    if (!ParentComponent) {
                        console.error(`Component for messageType: ${message.messageType} not found.`);
                        return;
                    }
                    const elementParent = React.createElement(ParentComponent, { key: __utils.getUuidv4(), ...getProps(), ...message, ...message.props, local: true }, elementChilds);
                    const chatMessage = React.createElement(ChatMessage, { key: __utils.getUuidv4() }, elementParent);
                    chatMessages.push(chatMessage);
                } catch (error) {
                    console.error(`Error creating parent element for message type: ${message.messageType}`, error);
                }
            });

            setContent(chatMessages);
        },
        [getProps],
    );

    const initFromLocalStorage = useCallback(
        () => {
            if (!chatId) {
                console.error("Chat ID is undefined, cannot initialize from local storage.");
                return;
            }

            const chats: any = __chats.get();

            //console.log('Chats from local storage:', JSON.stringify(chats));

            if (!chats[chatId]) {
                console.error(`No chat found for chatId: ${chatId}`);
                return;
            }

            formatChat(chats[chatId]);

            const lastMessageNo = __chats.getLastMessageNo(chatId);
            if (lastMessageNo) {
                messageNo.set(lastMessageNo);
                messageNo.increment();
            }

            // Set the bot status to finished
            __bot.setStatusFinished();

        },
        [formatChat, chatId, messageNo],
    );

    useEffect(() => {
        if (chatId) {
            initListeners();
            initFromLocalStorage();
        }
    }, [initListeners, initFromLocalStorage, chatId]);

    const resloveChat = useCallback(
        (chatMsg: IChatMsgAPI) => {
            const { userId, userName, userPhoto } = chatMsg;
            const userProps = {
                userId,
                userName,
                userPhoto
            };

            __callbacks.msgFragmentEx.trigger(
                new MsgFragmentNewChild({
                    messageType: chatMsg.messageType,
                    fragmentType: chatMsg.child.componentName,
                    fragment: chatMsg.fragment,
                    ...userProps,
                    props: { ...chatMsg.child.props },
                    ...getProps()
                })
            )
                .onEmpty(() => {
                    const Component = (components as any)[chatMsg.child.componentName];
                    if (!Component) {
                        return;
                    }
                    const elementChild = React.createElement(Component, { key: __utils.getRandomIdShort(), ...getProps(), ...chatMsg.child.props, id: __utils.getUuidv4(), type: chatMsg.child.componentName });
                    const ParentComponent = (components as any)[chatMsg.messageType];
                    if (!ParentComponent) {
                        return;
                    }
                    const elementParent = React.createElement(ParentComponent, { key: __utils.getUuidv4(), time: chatMsg.time, id: __utils.getUuidv4(), ...getProps(), ...userProps }, [elementChild]);
                    push(elementParent);
                });
        },
        [push, getProps]
    );

    useEffect(() => {
        if (!chatId) {
            return;
        }

        const chats = __chatQueue.resolve(chatId);
        chats?.forEach((chatMsg: IChatMsgAPI) => {
            resloveChat(chatMsg);
        });

        callbackIds.onUnreadMsgs = __callbacks.onUnreadMsgs((ureadMsgs: IUnreadMsg) => {
            if (ureadMsgs.chatId !== chatId) return;

            if (ureadMsgs.unreadMsgs > 0) {
                const chats = __chatQueue.resolve(chatId);
                chats.forEach((chatMsg: IChatMsgAPI) => {
                    resloveChat(chatMsg);
                });
            }
        });
    }, [chatId, resloveChat]);

    useEffect(() => {
        if (chatId) {
            __chats.setCurrentChatId(chatId);
        }
    }, [chatId]);

    useEffect(() => {
        setTimeout(() => {
            __callbacks.scrollChatBottom.trigger("instant");
        }, 500);
    }, []);

    useEffect(() => {
        return () => {
            __callbacks.chatInputSubmit.remove(callbackIds.chatInputSubmit);
            __callbacks.botResponse.remove(callbackIds.onBotResponse);
            __callbacks.fileUpload.remove(callbackIds.onFileUpload);
            __callbacks.unreadMsgs.remove(callbackIds.onUnreadMsgs);
            __callbacks.messageFragment.destroyCallback();
            __callbacks.msgFragmentEx.destroyCallback();
            currentMsg.messageNo = 0;
            __chats.setCurrentChatId("");
        }
    }, [chatId]);

    return (
        <div ref={container} className={styles.container}>
            {content}
        </div>
    );
}

Continue.defaultProps = {
    chatId: '',
};
